<template>
  <div class="edit-wishlist">
    <ListNameComponent
      :name="name"
      :label="fieldLabel"
      :error="inputError"
      class="edit-wishlist__input"
      @input-is-empty="removeErrorResponse"
      @set-name-valid="setNameValid"
      @set-name="setName"
    />
    <ListTypeComponent
      :list-type="listType"
      :disclaimer="disclaimer"
      @list-type-updated="updateListType"
    />

    <CdrButton
      v-if="saveButton.enabled"
      :disabled="!validName || inputError || isPublic === ''"
      class="edit-wishlist__save-button"
      data-ui="edit-wishlist-button"
      @click="updateWishlist"
    >
      {{ saveButton.text }}
    </CdrButton>

    <CdrButton
      v-if="cancelButton.enabled"
      data-ui="update-wishlist-cancel"
      modifier="secondary"
      @click="handleCancel"
    >
      {{ cancelButton.text }}
    </CdrButton>

    <CdrLink
      v-if="deleteLink.enabled && !isPrimary"
      aria-haspopup="dialog"
      tag="button"
      class="edit-wishlist__delete-link"
      data-ui="wishlist-deleteLink"
      modifier="standalone"
      @click="handleDelete"
    >
      {{ deleteLink.text }}
    </CdrLink>

    <CdrModal
      label="Delete wish list"
      aria-described-by="description"
      :opened="isModalOpen"
      @closed="cancelDelete"
    >
      <template #title>
        <h3 class="edit-wishlist__modal-title">Are you sure you want to delete "{{ listName }}"?</h3>
      </template>
      <CdrText id="description" class="edit-wishlist__modal-description" tag="p">
        This action cannot be undone.
      </CdrText>
      <div class="edit-wishlist__modal-buttons">
        <CdrButton
          v-if="deleteLink.enabled && !isPrimary"
          data-ui="wishlist-deleteLink"
          class="edit-wishlist__modal-button"
          @click="deleteWishlist"
        >
          {{ deleteLink.text }}
        </CdrButton>
        <CdrButton
          v-if="cancelButton.enabled"
          data-ui="update-wishlist-cancel"
          modifier="secondary"
          @click="cancelDelete"
        >
          {{ cancelButton.text }}
        </CdrButton>
      </div>
    </CdrModal>
  </div>
</template>

<script>
import { CdrLink, CdrButton, CdrModal, CdrText } from '@rei/cedar';
import { mapActions, mapWritableState } from 'pinia';
import ListNameComponent from '../common/ListNameComponent.vue';
import ListTypeComponent from '../common/ListTypeComponent.vue';
import { useWishlistStore } from '../../stores';
import metrics from '../../utils/metrics';

export default {
  name: 'EditAndDeleteWishlistComponent',
  components: {
    CdrButton,
    CdrLink,
    CdrModal,
    CdrText,
    ListNameComponent,
    ListTypeComponent,
  },
  props: {
    listId: {
      type: Number,
      required: true,
    },
    listName: {
      type: String,
      required: true,
    },
    publicList: {
      type: Boolean,
      required: true,
    },
    isPrimary: {
      type: Boolean,
      required: true,
    },
    fieldLabel: {
      type: String,
      required: true,
    },
    saveButton: {
      type: Object,
      required: true,
    },
    cancelButton: {
      type: Object,
      required: true,
    },
    deleteLink: {
      type: Object,
      required: true,
    },
    listType: {
      type: String,
      required: true,
    },
    disclaimer: {
      type: String,
      required: true,
    },
    errorMessages: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      name: this.listName,
      isModalOpen: false,
      isPublic: '',
      listNameValidation: null,
      validName: !!this.listName,
    };
  },
  computed: {
    ...mapWritableState(useWishlistStore, ['errorKey']),
    inputError() {
      const { listNameValidation } = this;
      return listNameValidation && this.errorMessages[listNameValidation];
    },
  },
  methods: {
    ...mapActions(useWishlistStore, ['updateList', 'deleteList']),
    updateWishlist() {
      metrics.metricsClick(this.saveButton.text);
      const { listId } = this;

      this.updateList({
        listId,
        name: this.name,
        isPublic: this.isPublic,
      }).then((APIError) => {
        if (!APIError) {
          this.$emit('list-name-updated', this.name);
          this.$emit('list-type-updated', this.isPublic);
          this.$emit('close');
        }
        this.listNameValidation = APIError;
      });
    },
    deleteWishlist() {
      metrics.metricsClick('Delete confirm');
      const { listId } = this;
      this.deleteList(listId);
    },
    removeErrorResponse() {
      this.listNameValidation = null;
      this.errorKey = null;
    },
    updateListType(value) {
      this.isPublic = typeof value === 'boolean' ? value : '';
    },
    setNameValid(value) {
      this.validName = value;
      if (!this.validName) {
        this.listNameValidation = 'ILLEGAL_CHARACTERS';
      } else {
        this.removeErrorResponse();
      }
    },
    setName(value) {
      this.name = value;
    },
    handleCancel() {
      metrics.metricsClick(this.cancelButton.text);
      this.$emit('close');
    },
    handleDelete() {
      this.isModalOpen = true;
      metrics.metricsClick(this.deleteLink.text);
    },
    cancelDelete() {
      this.isModalOpen = false;
      metrics.metricsClick('Delete cancel');
    },
  },
};
</script>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.edit-wishlist {
  & > div:first-child {
    margin-top: $cdr-space-three-quarter-x;
  }

  &__save-button {
    margin-right: $cdr-space-quarter-x;
  }

  &__delete-link {
    display: block;
    margin-top: $cdr-space-one-and-a-half-x;
  }

  &__modal {
    &-description {
      margin: $cdr-space-one-x 0;
    }
    &-title {
      @include cdr-text-heading-serif-600;
    }

    &-buttons {
      display: flex;
      flex-direction: column;
      margin-top: $cdr-space-one-and-a-half-x;
    }

    &-button {
      margin-bottom: $cdr-space-one-x;
    }
  }
}

@media screen and (min-width: $cdr-breakpoint-sm) {
  .edit-wishlist {
    &__modal {
      &-buttons {
        flex-direction: row;
      }

      &-button {
        margin-bottom: 0;
        margin-right: $cdr-space-quarter-x;
      }
    }
  }
}
</style>
